/**
 * Block validation rule messages (ADR-0101): one line per issue, with the
 * rule's fix as an inline link and, for an error, the "Behåll ändå"
 * override. Used next to the field (short text) and in the summary (full).
 */

import type { RuleIssue } from '@hyranai/regta-document-ir';

export type RuleActions = {
  onApplyFix: (issue: RuleIssue) => void;
  onOverride: (issue: RuleIssue, keep: boolean) => void;
  disabled?: boolean;
};

const tone = (issue: RuleIssue): string =>
  issue.severity === 'warning'
    ? 'text-amber-800'
    : issue.overridden
      ? 'text-stone-600'
      : 'text-red-700';

export function RuleMessage({
  issue,
  variant,
  actions,
  onLocate,
}: {
  issue: RuleIssue;
  variant: 'field' | 'summary';
  actions: RuleActions;
  /** Summary only: jump to the field. */
  onLocate?: (issue: RuleIssue) => void;
}) {
  const text = variant === 'field' ? issue.fieldMessage : issue.summaryMessage;
  const link = 'underline underline-offset-2 hover:no-underline disabled:opacity-50 disabled:no-underline';
  return (
    <span
      role={issue.severity === 'error' && !issue.overridden ? 'alert' : undefined}
      data-rule-id={issue.id}
      data-rule-severity={issue.severity}
      className={`text-[11px] ${tone(issue)}`}
    >
      {onLocate ? (
        <button type="button" className="text-left hover:underline" onClick={() => onLocate(issue)}>
          {text}
        </button>
      ) : (
        text
      )}
      {issue.overridden && ' (behålls ändå)'}
      {issue.fix && !issue.overridden && (
        <>
          {' · '}
          <button type="button" className={link} disabled={actions.disabled} onClick={() => actions.onApplyFix(issue)}>
            {issue.fix.label}
          </button>
        </>
      )}
      {issue.severity === 'error' && (
        <>
          {' · '}
          <button
            type="button"
            className={link}
            disabled={actions.disabled}
            onClick={() => actions.onOverride(issue, !issue.overridden)}
          >
            {issue.overridden ? 'Ångra' : 'Behåll ändå'}
          </button>
        </>
      )}
    </span>
  );
}
